/**
 * Dashboard — greeting, 4-up metrics, two-column grid.
 * Implements designs/Dashboard.html.
 *
 * Layout (left → right, top → bottom):
 *   • Greeting + quick-create row
 *   • 4 metric cards (with sparkline on "Due this week")
 *   • Two-column grid
 *       LEFT  — My Tasks panel (week / today / all)
 *             — Team workload bars
 *       RIGHT — Team activity feed (capped height, scrolls in place)
 *   • Recently updated projects — full-width row below the grid
 *     (responsive auto-fill card grid)
 *
 * NOTE (DONE_WITH_CONCERNS): the design shows per-metric sparklines, "+18% vs
 * May" deltas, and "2 due soon" sub-text. The current
 * /api/metrics/dashboard endpoint only exposes `completedSpark`. Until the
 * endpoint is extended, missing deltas / footers are simply omitted.
 *
 * NOTE (DONE_WITH_CONCERNS): the project cards rely on ProjectRow's optional
 * `completedCount` / `totalCount` / `members` fields (server-populated). If
 * either is missing we degrade gracefully (no bar / no avatars).
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api } from "@/lib/api";
import { parseDateLocal } from "@/lib/format";
import { queryKeys } from "@/lib/queryKeys";
import { ActivityFeed } from "@/components/ActivityFeed";
import { Skeleton } from "@/components/Skeleton";
import { Icon } from "@/components/Icon";
import { AvatarGroup } from "@/components/AvatarGroup";
import { MetricCard } from "@/components/MetricCard";
import { PasskeyPrompt } from "@/components/PasskeyPrompt";
import { PriorityChip } from "@/components/PriorityChip";
import type { TaskRow } from "@shared/types";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function dateLine(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Classify a due date string for the .tr-due {soon|over|""} variants. */
function dueClass(due: string | null): "soon" | "over" | "" {
  const parsed = parseDateLocal(due);
  if (!parsed) return "";
  const today = startOfDay(new Date());
  const d = startOfDay(parsed);
  const diffDays = Math.round(
    (d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (diffDays < 0) return "over";
  if (diffDays <= 2) return "soon";
  return "";
}

function dueLabel(due: string | null): string {
  const d = parseDateLocal(due);
  if (!d) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

type TaskFilter = "week" | "today" | "all";

function filterTasks(tasks: TaskRow[], f: TaskFilter): TaskRow[] {
  if (f === "all") return tasks;
  const today = startOfDay(new Date());
  const horizon = new Date(today);
  horizon.setDate(today.getDate() + (f === "today" ? 0 : 7));
  return tasks.filter((t) => {
    const parsed = parseDateLocal(t.dueDate);
    if (!parsed) return false;
    const d = startOfDay(parsed);
    if (f === "today") return d.getTime() === today.getTime();
    // week filter: due on or before today + 7 days, and not earlier than today
    return d.getTime() >= today.getTime() && d.getTime() <= horizon.getTime();
  });
}

// ─── TaskRow (My Tasks panel) ────────────────────────────────────────────────

function MyTaskRow({ task }: { task: TaskRow }) {
  const done = task.status === "done";
  const dc = dueClass(task.dueDate);
  return (
    <Link
      to="/tasks/$taskId"
      params={{ taskId: task.id }}
      className={`task-row${done ? " done" : ""}`}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <span className="cb" aria-hidden="true">
        {done ? <Icon name="check" size={12} /> : null}
      </span>
      <span className="tr-title">{task.title}</span>
      <PriorityChip level={task.priority} />
      {task.dueDate ? (
        <span className={`tr-due${dc ? ` ${dc}` : ""}`}>
          {dueLabel(task.dueDate)}
        </span>
      ) : null}
    </Link>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export function Dashboard({ userName }: { userName: string }) {
  const [taskFilter, setTaskFilter] = useState<TaskFilter>("week");

  const metrics = useQuery({
    queryKey: queryKeys.metricsDashboard(),
    queryFn: api.metricsDashboard,
  });

  const workload = useQuery({
    queryKey: queryKeys.teamWorkload(),
    queryFn: api.teamWorkload,
  });

  const projects = useQuery({
    queryKey: queryKeys.projectsRecent(4),
    queryFn: () => api.projectsRecent(4),
  });

  const activity = useQuery({
    // Use a dashboard-scoped key so this non-infinite preview doesn't share a
    // cache entry with the /activity page's useInfiniteQuery — same key with
    // different shape (`{items}` vs `{pages,pageParams}`) crashes the
    // InfiniteQueryObserver on transition.
    queryKey: queryKeys.activityGlobalDashboard(),
    queryFn: () => api.activityGlobal(),
  });

  const myTasks = useQuery({
    queryKey: queryKeys.meTasks(),
    queryFn: api.myTasks,
  });

  const filteredTasks = useMemo(
    () => (myTasks.data ? filterTasks(myTasks.data.tasks, taskFilter) : []),
    [myTasks.data, taskFilter],
  );

  // Max openCount across the team, used to scale the workload bars so the
  // busiest member's bar reaches 100%. Guard against 0 with `|| 1`.
  const workloadMax = useMemo(() => {
    if (!workload.data) return 1;
    return Math.max(1, ...workload.data.members.map((m) => m.openCount));
  }, [workload.data]);

  return (
    <div className="dash">
      <PasskeyPrompt />
      {/* ── Greeting ─────────────────────────────────────────────────────── */}
      <div className="greet">
        <div>
          <h1 data-testid="dashboard-greeting">
            {greeting()}, {userName} 👋
          </h1>
          <div className="date">{dateLine()}</div>
        </div>
        <div className="qc">
          <Link to="/projects/new" className="btn btn-secondary">
            <Icon name="folder" size={14} />
            New project
          </Link>
          {/* "New task" needs a project in scope. We surface the user's
              most-recently-updated project as the default destination; the
              new-project route is the graceful fallback when they have none
              yet. */}
          {projects.data?.items[0] ? (
            <Link
              to="/projects/$projectId/tasks/new"
              params={{ projectId: projects.data.items[0].id }}
              className="btn btn-primary"
            >
              <Icon name="plus" size={14} />
              New task
            </Link>
          ) : (
            <Link to="/projects/new" className="btn btn-primary">
              <Icon name="plus" size={14} />
              New task
            </Link>
          )}
        </div>
      </div>

      {/* ── Metrics row ──────────────────────────────────────────────────── */}
      <div className="metric-row">
        {metrics.isLoading ? (
          <Skeleton count={4} width={220} height={104} />
        ) : metrics.isError ? (
          <span className="t-sm t-danger">Could not load metrics.</span>
        ) : !metrics.data ? null : (
          (() => {
            const m = metrics.data;
            const delta =
              m.completedPrevMonth === 0
                ? null
                : Math.round(
                    ((m.completedMonth - m.completedPrevMonth) /
                      m.completedPrevMonth) *
                      100,
                  );
            const completedFooter =
              delta === null
                ? { text: "no prior data", tone: "flat" as const }
                : delta === 0
                  ? { text: "flat vs last month", tone: "flat" as const }
                  : delta > 0
                    ? {
                        text: `+${Math.abs(delta)}% vs last month`,
                        tone: "up" as const,
                      }
                    : {
                        text: `-${Math.abs(delta)}% vs last month`,
                        tone: "warn" as const,
                      };
            return (
              <>
                <MetricCard
                  label="My open tasks"
                  value={m.myOpen}
                  footer={{
                    text: `${m.myOpenDueSoon} due soon`,
                    tone: "flat",
                  }}
                />
                <MetricCard
                  label="Due this week"
                  value={m.dueThisWeek}
                  spark={m.completedSpark}
                />
                <MetricCard
                  label="Completed · this month"
                  value={m.completedMonth}
                  spark={m.completedSpark}
                  footer={completedFooter}
                />
                <MetricCard
                  label="Team overdue"
                  value={m.teamOverdue}
                  footer={{
                    text: `across ${m.teamOverdueProjectCount} projects`,
                    tone: "flat",
                  }}
                />
              </>
            );
          })()
        )}
      </div>

      {/* ── Main grid ────────────────────────────────────────────────────── */}
      <div className="dash-grid">
        {/* LEFT — My Tasks + Team workload */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* My Tasks */}
          <div className="panel">
            <div className="panel-head">
              <h3>My Tasks</h3>
              <div className="seg">
                <button
                  type="button"
                  className={taskFilter === "week" ? "on" : undefined}
                  onClick={() => setTaskFilter("week")}
                >
                  This week
                </button>
                <button
                  type="button"
                  className={taskFilter === "today" ? "on" : undefined}
                  onClick={() => setTaskFilter("today")}
                >
                  Today
                </button>
                <button
                  type="button"
                  className={taskFilter === "all" ? "on" : undefined}
                  onClick={() => setTaskFilter("all")}
                >
                  All
                </button>
              </div>
            </div>
            {myTasks.isLoading ? (
              <div style={{ padding: "16px 20px" }}>
                <Skeleton count={4} height={42} />
              </div>
            ) : myTasks.isError ? (
              <p
                className="t-sm"
                style={{ padding: "16px 20px", color: "var(--st-danger-ink)" }}
              >
                Could not load tasks.
              </p>
            ) : !myTasks.data || filteredTasks.length === 0 ? (
              <div className="empty">
                <div className="t-sm">Nothing here — enjoy the breather.</div>
              </div>
            ) : (
              <div>
                {filteredTasks.map((t) => (
                  <MyTaskRow key={t.id} task={t} />
                ))}
              </div>
            )}
          </div>

          {/* Team workload */}
          <div className="panel">
            <div className="panel-head">
              <h3>Team workload</h3>
              <span className="t-xs" style={{ marginLeft: "auto" }}>
                active tasks
              </span>
            </div>

            {workload.isLoading ? (
              <div style={{ padding: "8px 20px" }}>
                <Skeleton count={5} height={32} />
              </div>
            ) : workload.isError ? (
              <p
                className="t-sm"
                style={{ padding: "16px 20px", color: "var(--st-danger-ink)" }}
              >
                Could not load workload.
              </p>
            ) : !workload.data || workload.data.members.length === 0 ? (
              <p
                className="t-sm"
                style={{ padding: "16px 20px", color: "var(--muted)" }}
              >
                No team members.
              </p>
            ) : (
              <div className="wl-list">
                {workload.data.members.map((m) => {
                  const pct = Math.round((m.openCount / workloadMax) * 100);
                  // Per-member bar tone follows the load classification so the
                  // bars are visually distinguishable even when names overflow.
                  const barColor =
                    m.load === "heavy"
                      ? "var(--orange-500)"
                      : m.load === "balanced"
                        ? "var(--st-todo)"
                        : "var(--green-500)";
                  return (
                    <div
                      key={m.userId}
                      className="wl-row"
                      data-testid={`workload-row-${m.userId}`}
                    >
                      <Link
                        to="/team/$userId"
                        params={{ userId: m.userId }}
                        className="wl-name"
                        style={{ color: "inherit", textDecoration: "none" }}
                      >
                        {m.name}
                      </Link>
                      <div
                        className="wl-bar"
                        role="progressbar"
                        aria-valuenow={m.openCount}
                        aria-valuemin={0}
                        aria-valuemax={workloadMax}
                        aria-label={`${m.name} workload`}
                      >
                        <i style={{ width: `${pct}%`, background: barColor }} />
                      </div>
                      <span className="wl-n">{m.openCount}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Team activity */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Team activity */}
          <div className="panel">
            <div className="panel-head">
              <h3>Team activity</h3>
              <Link to="/activity" className="ph-link">
                View all →
              </Link>
            </div>
            <div className="feed">
              {activity.isLoading ? (
                <Skeleton count={3} height={56} />
              ) : activity.isError ? (
                <p className="t-sm t-danger">Could not load activity.</p>
              ) : !activity.data ? (
                <p className="t-sm t-muted">No recent activity.</p>
              ) : (
                <ActivityFeed
                  items={activity.data.items}
                  hasMore={false}
                  onLoadMore={() => {}}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recently updated projects — full-width row */}
      <div className="panel dash-projects">
        <div className="panel-head">
          <h3>Recently updated projects</h3>
          <Link to="/projects" className="ph-link">
            All projects →
          </Link>
        </div>

        {projects.isLoading ? (
          <div style={{ padding: "16px 20px" }}>
            <Skeleton count={4} height={120} />
          </div>
        ) : projects.isError ? (
          <p
            className="t-sm"
            style={{ padding: "16px 20px", color: "var(--st-danger-ink)" }}
          >
            Could not load projects.
          </p>
        ) : !projects.data || projects.data.items.length === 0 ? (
          <p
            className="t-sm"
            style={{ padding: "16px 20px", color: "var(--muted)" }}
          >
            No projects yet.
          </p>
        ) : (
          <div className="proj-cards">
            {projects.data.items.slice(0, 8).map((p) => {
              const completed = p.completedCount ?? 0;
              const total = p.totalCount ?? 0;
              const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
              return (
                <Link
                  key={p.id}
                  to="/projects/$projectId"
                  params={{ projectId: p.id }}
                  className="pc"
                >
                  <div className="pc-top">
                    <span
                      className="proj-dot"
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: p.color ?? "var(--green-500)",
                        display: "inline-block",
                      }}
                    />
                    <span className="pc-name">{p.name}</span>
                  </div>
                  {p.description ? (
                    <div className="pc-desc">{p.description}</div>
                  ) : (
                    <div className="pc-desc" />
                  )}
                  {total > 0 ? (
                    <>
                      <div className="progress-lab">
                        <span>{pct}%</span>
                        <span>{total} tasks</span>
                      </div>
                      <div className="progress" style={{ marginBottom: 12 }}>
                        <i style={{ width: `${pct}%` }} />
                      </div>
                    </>
                  ) : null}
                  <div className="pc-foot">
                    <span className="pill pill-doing">
                      <span className="dot" />
                      {p.status === "archived" ? "Archived" : "Active"}
                    </span>
                    {p.members && p.members.length > 0 ? (
                      <AvatarGroup
                        users={p.members.map((m) => ({
                          id: m.id,
                          name: m.name ?? "Unknown",
                          image: m.image ?? null,
                        }))}
                        max={3}
                        size={20}
                      />
                    ) : null}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
